import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, X, ArrowLeft, ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import CrewActionVideo from "../components/CrewActionVideo";
import RealJobsGallery from "../components/RealJobsGallery";
import BeforeAfter from "../components/BeforeAfter";
import { useMedia } from "../hooks/useMedia";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "commercial-windows", label: "Commercial Windows" },
  { id: "pressure-washing", label: "Pressure Washing" },
  { id: "parking-lots", label: "Parking Lots" },
  { id: "storefronts", label: "Storefronts" },
  { id: "specialty", label: "Specialty" },
  { id: "before-after", label: "Before / After" },
];

// Static gallery items — mapped to admin-uploaded photos. Each item has a category.
const ITEMS = [
  { id: "pressure-washing", cat: "pressure-washing", label: "Cast-Iron Storefront · SoHo" },
  { id: "sidewalk-cleaning", cat: "pressure-washing", label: "Sidewalk Restoration · Flatiron" },
  { id: "window-cleaning", cat: "commercial-windows", label: "Restaurant Windows · Flatiron" },
  { id: "awning-cleaning", cat: "storefronts", label: "Canopy Cleaning · Chelsea" },
  { id: "parking-lot-striping", cat: "parking-lots", label: "Retail Plaza Striping · Long Island" },
  { id: "parking-lot-sweeping", cat: "parking-lots", label: "Corporate Sweeping · Charlotte" },
  { id: "snow-removal", cat: "specialty", label: "Storm Response · Brooklyn" },
  { id: "graffiti-removal", cat: "specialty", label: "Alley Facade Restoration · Williamsburg" },
  { id: "dumpster-cleaning", cat: "specialty", label: "Dumpster Corral · Astoria" },
  { id: "outdoor-lighting", cat: "storefronts", label: "Permanent Architectural Lighting · Tribeca" },
  { id: "pest-control", cat: "specialty", label: "Perimeter Treatment · Long Island City" },
];

const PAIRS = [
  { key: "transform-1", label: "Cast-Iron Storefront · SoHo", note: "Hot-water surface clean at dawn." },
  { key: "transform-2", label: "Restaurant Windows · Flatiron", note: "DI water-fed pole restoration." },
  { key: "transform-3", label: "Retail Plaza Parking · Long Island", note: "Sealcoat + full re-stripe." },
  { key: "transform-4", label: "Alley Facade · Williamsburg", note: "Graffiti removal + anti-graffiti coat." },
];

export default function Gallery() {
  const { url } = useMedia();
  const [cat, setCat] = useState("all");
  const [lightbox, setLightbox] = useState(null); // { type: 'single'|'pair', idx }

  const filteredItems = useMemo(
    () => (cat === "all" ? ITEMS : ITEMS.filter((it) => it.cat === cat)),
    [cat]
  );

  const showPairs = cat === "all" || cat === "before-after";
  const showItems = cat !== "before-after";

  // Lightbox nav
  useEffect(() => {
    function onKey(e) {
      if (!lightbox) return;
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight" && lightbox.type === "pair") {
        setLightbox({ ...lightbox, idx: (lightbox.idx + 1) % PAIRS.length });
      }
      if (e.key === "ArrowLeft" && lightbox.type === "pair") {
        setLightbox({ ...lightbox, idx: (lightbox.idx - 1 + PAIRS.length) % PAIRS.length });
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  return (
    <div className="min-h-screen" data-testid="gallery-page">
      <PageHero
        eyebrow="The Gallery"
        title={<>Documented. <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>Not decorated.</span></>}
        subtitle="Every job leaves our care photographed. Filter by service, tap any tile to enlarge, or drag the before/after sliders to see the shift."
        image={url("pressure-washing") || url("outdoor-lighting")}
        kicker="Ninety percent of our clients hire us after seeing this page."
      />

      {/* Cinematic crew action video */}
      <CrewActionVideo />

      {/* Real field jobs — pulled from crew's live photo album */}
      <RealJobsGallery />

      {/* Filters */}
      <section className="py-14" style={{ background: "var(--bg)", borderBottom: "1px solid var(--line)" }}>
        <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-14 flex flex-wrap gap-3">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setCat(c.id)}
              className="font-mono text-[10px] uppercase tracking-[0.36em] px-5 py-3 transition-all duration-500"
              style={{
                borderRadius: "var(--r-full)",
                border: `1px solid ${cat === c.id ? "var(--gold)" : "var(--line)"}`,
                background: cat === c.id ? "rgba(198,168,124,0.12)" : "transparent",
                color: cat === c.id ? "var(--gold)" : "var(--text-2)",
              }}
              data-testid={`gallery-filter-${c.id}`}
              data-cursor="hover"
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      {/* Before/After pairs */}
      {showPairs && (
        <section className="py-24 md:py-28" style={{ background: "var(--bg)" }}>
          <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-14">
            <div className="overline mb-8">Before / After</div>
            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              {PAIRS.map((p, i) => (
                <motion.div
                  key={p.key}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.9, delay: i * 0.08 }}
                  onClick={() => setLightbox({ type: "pair", idx: i })}
                  className="cursor-pointer"
                  data-testid={`gallery-pair-${p.key}`}
                  data-cursor="hover"
                  data-cursor-label="Expand"
                >
                  <BeforeAfter before={url(`${p.key}-before`)} after={url(`${p.key}-after`)} label={p.label} />
                  <p className="mt-4 font-body text-sm" style={{ color: "var(--text-3)" }}>{p.note}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Single-image tiles */}
      {showItems && filteredItems.length > 0 && (
        <section className="py-24 md:py-28" style={{ background: "var(--bg-2)", borderTop: "1px solid var(--line)" }}>
          <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-14">
            <div className="overline mb-8">Field Record</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filteredItems.map((it, i) => (
                <motion.button
                  key={it.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, delay: (i % 3) * 0.06 }}
                  onClick={() => setLightbox({ type: "single", src: url(it.id), label: it.label })}
                  className="glass-card overflow-hidden group text-left"
                  data-testid={`gallery-item-${it.id}`}
                  data-cursor="hover"
                  data-cursor-label="Open"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    {url(it.id) ? (
                      <img src={url(it.id)} alt={it.label} className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" draggable={false} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.36em]" style={{ background: "rgba(198,168,124,0.05)", color: "var(--text-3)" }}>
                        Loading
                      </div>
                    )}
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.85) 100%)" }} />
                    <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between">
                      <div className="font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--text-2)" }}>{it.label}</div>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[80] flex items-center justify-center p-6 md:p-14"
            style={{ background: "rgba(2,2,2,0.94)", backdropFilter: "blur(16px)" }}
            onClick={() => setLightbox(null)}
            data-testid="lightbox"
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center"
              style={{ borderRadius: "var(--r-full)", border: "1px solid var(--line-gold)", color: "var(--gold)" }}
              aria-label="Close"
              data-testid="lightbox-close"
            >
              <X size={16} />
            </button>

            <div className="max-w-[1400px] w-full" onClick={(e) => e.stopPropagation()}>
              {lightbox.type === "single" ? (
                <>
                  <img src={lightbox.src} alt={lightbox.label} className="w-full max-h-[80vh] object-contain" style={{ borderRadius: "var(--r-2xl)", border: "1px solid var(--line-gold)" }} />
                  <div className="mt-6 font-mono text-[11px] uppercase tracking-[0.4em]" style={{ color: "var(--text-2)" }}>{lightbox.label}</div>
                </>
              ) : (
                <>
                  <BeforeAfter before={url(`${PAIRS[lightbox.idx].key}-before`)} after={url(`${PAIRS[lightbox.idx].key}-after`)} label={PAIRS[lightbox.idx].label} />
                  <div className="mt-6 flex items-center justify-between">
                    <div className="font-mono text-[11px] uppercase tracking-[0.4em]" style={{ color: "var(--text-2)" }}>{PAIRS[lightbox.idx].label}</div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setLightbox({ ...lightbox, idx: (lightbox.idx - 1 + PAIRS.length) % PAIRS.length })}
                        className="w-11 h-11 flex items-center justify-center"
                        style={{ borderRadius: "var(--r-full)", border: "1px solid var(--line-gold)", color: "var(--gold)" }}
                      >
                        <ArrowLeft size={14} />
                      </button>
                      <button
                        onClick={() => setLightbox({ ...lightbox, idx: (lightbox.idx + 1) % PAIRS.length })}
                        className="w-11 h-11 flex items-center justify-center"
                        style={{ borderRadius: "var(--r-full)", border: "1px solid var(--line-gold)", color: "var(--gold)" }}
                      >
                        <ArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <section className="py-28 md:py-36 text-center" style={{ background: "var(--bg)" }}>
        <h2 className="font-display text-5xl md:text-7xl leading-[0.95] max-w-4xl mx-auto px-6" style={{ letterSpacing: "-0.035em" }}>
          The next tile is <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>your property.</span>
        </h2>
        <div className="mt-12 flex flex-wrap justify-center gap-5">
          <Link to="/quote" className="btn-glow" data-cursor="hover" data-testid="gallery-cta-quote">
            Request a Quote <ArrowUpRight size={13} />
          </Link>
        </div>
      </section>
    </div>
  );
}
